import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { HelpCircle, Plus, FileText, CheckCircle2, Clock, X, Loader2, AlertCircle, Eye, ChevronLeft, ChevronRight } from 'lucide-react';
import api from '../services/api';

function Support() {
  const [tickets, setTickets] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  
  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;
  
  // Form State
  const [subject, setSubject] = useState('');
  const [priority, setPriority] = useState('MEDIUM');
  const [description, setDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  const loadTickets = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await api.getUserTickets();
      setTickets(data.tickets || []);
    } catch (err) {
      setErrorMsg('Failed to load tickets: ' + err.message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTickets();
  }, [loadTickets]);

  // Pagination Calculations
  const totalPages = Math.ceil(tickets.length / itemsPerPage) || 1;
  const indexOfLastTicket = currentPage * itemsPerPage;
  const indexOfFirstTicket = indexOfLastTicket - itemsPerPage;
  const currentTickets = tickets.slice(indexOfFirstTicket, indexOfLastTicket);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(1);
    }
  }, [tickets.length, totalPages, currentPage]);

  const handleCreateTicket = async (e) => {
    e.preventDefault();
    setErrorMsg('');
    setSuccessMsg('');
    
    if (!subject.trim()) {
      setErrorMsg('Subject is required.');
      return;
    }

    setIsSubmitting(true);
    try {
      await api.createTicket(subject, priority, description);
      setSuccessMsg('Support ticket created successfully!');
      setIsCreating(false);
      setSubject('');
      setDescription('');
      setPriority('MEDIUM');
      setCurrentPage(1);
      loadTickets();
    } catch (err) {
      setErrorMsg(err.message || 'Failed to create ticket');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div style={{ padding: '2rem', maxWidth: '1200px', margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
        <div>
          <h2 style={{ fontSize: '1.75rem', fontWeight: '700', color: 'var(--text-main)', margin: '0 0 0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <HelpCircle size={28} color="var(--primary)" />
            Support Center
          </h2>
          <p style={{ color: 'var(--text-muted)', margin: 0 }}>View your support tickets or open a new one.</p>
        </div>
        {!isCreating && (
          <button 
            className="btn btn-primary" 
            style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.6rem 1.2rem' }}
            onClick={() => setIsCreating(true)}
          >
            <Plus size={18} /> New Ticket
          </button>
        )}
      </div>

      {errorMsg && (
        <div style={{ background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', padding: '1rem', borderRadius: '8px', marginBottom: '1.5rem', border: '1px solid rgba(239, 68, 68, 0.2)' }}>
          {errorMsg}
        </div>
      )}

      {successMsg && (
        <div style={{ background: 'rgba(16, 185, 129, 0.1)', color: '#10b981', padding: '1rem', borderRadius: '8px', marginBottom: '1.5rem', border: '1px solid rgba(16, 185, 129, 0.2)' }}>
          {successMsg}
        </div>
      )}

      {isCreating && (
        <div style={{ background: 'var(--bg-card)', padding: '2rem', borderRadius: '12px', border: '1px solid var(--border-color)', marginBottom: '2rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
            <h3 style={{ margin: 0, fontSize: '1.25rem', color: 'var(--text-main)' }}>Create New Ticket</h3>
            <button onClick={() => setIsCreating(false)} style={{ background: 'transparent', border: 'none', color: 'var(--text-muted)', cursor: 'pointer' }}>
              <X size={24} />
            </button>
          </div>
          
          <form onSubmit={handleCreateTicket}>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', color: 'var(--text-main)' }}>Subject</label>
              <input 
                type="text" 
                value={subject} 
                onChange={(e) => setSubject(e.target.value)} 
                placeholder="Brief summary of your issue"
                style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', border: '1px solid var(--border-color)', background: 'var(--bg-input)', color: 'var(--text-main)' }}
                required
              />
            </div>
            
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', color: 'var(--text-main)' }}>Priority</label>
              <select 
                value={priority} 
                onChange={(e) => setPriority(e.target.value)}
                style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', border: '1px solid var(--border-color)', background: 'var(--bg-input)', color: 'var(--text-main)' }}
              >
                <option value="LOW">Low</option>
                <option value="MEDIUM">Medium</option>
                <option value="HIGH">High</option>
                <option value="URGENT">Urgent</option>
              </select>
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', color: 'var(--text-main)' }}>Description (Optional)</label>
              <textarea 
                value={description} 
                onChange={(e) => setDescription(e.target.value)} 
                placeholder="Provide any relevant details..."
                rows={4}
                style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', border: '1px solid var(--border-color)', background: 'var(--bg-input)', color: 'var(--text-main)', resize: 'vertical' }}
              />
            </div>

            <div style={{ display: 'flex', gap: '1rem' }}>
              <button type="submit" disabled={isSubmitting} className="btn btn-primary" style={{ padding: '0.75rem 2rem' }}>
                {isSubmitting ? <Loader2 size={18} className="spinning" /> : 'Submit Ticket'}
              </button>
              <button type="button" onClick={() => setIsCreating(false)} className="btn btn-secondary" style={{ padding: '0.75rem 2rem' }}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {!isCreating && (
        <div style={{ background: 'var(--bg-card)', borderRadius: '12px', border: '1px solid var(--border-color)', overflow: 'hidden' }}>
          {isLoading ? (
            <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--text-muted)' }}>
              <Loader2 size={32} className="spinning" style={{ margin: '0 auto 1rem', display: 'block' }} />
              Loading tickets...
            </div>
          ) : tickets.length === 0 ? (
            <div style={{ padding: '4rem 2rem', textAlign: 'center', color: 'var(--text-muted)' }}>
              <FileText size={48} style={{ opacity: 0.2, margin: '0 auto 1rem', display: 'block' }} />
              <p style={{ fontSize: '1.1rem', marginBottom: '0' }}>You have no support tickets.</p>
            </div>
          ) : (
            <>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--border-color)', background: 'var(--surface-hover)' }}>
                      <th style={{ padding: '1rem', fontWeight: '600', color: 'var(--text-muted)' }}>Ticket ID</th>
                      <th style={{ padding: '1rem', fontWeight: '600', color: 'var(--text-muted)' }}>Subject</th>
                      <th style={{ padding: '1rem', fontWeight: '600', color: 'var(--text-muted)' }}>Priority</th>
                      <th style={{ padding: '1rem', fontWeight: '600', color: 'var(--text-muted)' }}>Status</th>
                      <th style={{ padding: '1rem', fontWeight: '600', color: 'var(--text-muted)' }}>Date</th>
                      <th style={{ padding: '1rem', fontWeight: '600', color: 'var(--text-muted)', textAlign: 'center' }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentTickets.map(ticket => (
                      <tr key={ticket.id} style={{ borderBottom: '1px solid var(--border-color)', transition: 'background 0.2s' }}>
                        <td style={{ padding: '1rem', fontFamily: 'monospace', color: 'var(--text-muted)' }}>#{ticket.publicId}</td>
                        <td style={{ padding: '1rem', color: 'var(--text-main)', fontWeight: '500' }}>{ticket.subject}</td>
                        <td style={{ padding: '1rem' }}>
                          <span style={{ 
                            padding: '0.2rem 0.6rem', 
                            borderRadius: '4px', 
                            fontSize: '0.75rem',
                            fontWeight: '700',
                            background: ticket.priority === 'HIGH' || ticket.priority === 'URGENT' ? 'rgba(239, 68, 68, 0.15)' : 'rgba(34, 197, 94, 0.15)',
                            color: ticket.priority === 'HIGH' || ticket.priority === 'URGENT' ? '#ef4444' : '#4ade80'
                          }}>
                            {ticket.priority}
                          </span>
                        </td>
                        <td style={{ padding: '1rem' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', color: ticket.status === 'OPEN' ? '#ef4444' : ticket.status === 'IN_PROGRESS' ? '#f59e0b' : '#10b981', fontWeight: '600', fontSize: '0.85rem' }}>
                            {ticket.status === 'OPEN' ? <AlertCircle size={14} /> : ticket.status === 'IN_PROGRESS' ? <Clock size={14} /> : <CheckCircle2 size={14} />}
                            {ticket.status === 'OPEN' ? 'Open' : ticket.status === 'IN_PROGRESS' ? 'In Progress' : 'Resolved'}
                          </div>
                        </td>
                        <td style={{ padding: '1rem', color: 'var(--text-muted)', fontSize: '0.9rem' }}>
                          {new Date(ticket.createdAt).toLocaleDateString()}
                        </td>
                        <td style={{ padding: '1rem', textAlign: 'center' }}>
                          <button 
                            onClick={() => setSelectedTicket(ticket)}
                            title="View Ticket Details"
                            style={{
                              background: 'rgba(59, 130, 246, 0.1)',
                              border: '1px solid rgba(59, 130, 246, 0.3)',
                              color: '#3b82f6',
                              borderRadius: '6px',
                              padding: '0.4rem 0.75rem',
                              cursor: 'pointer',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '0.4rem',
                              fontSize: '0.85rem',
                              fontWeight: '500',
                              transition: 'all 0.2s'
                            }}
                          >
                            <Eye size={16} /> View
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination Bar */}
              {tickets.length > 0 && (
                <div style={{
                  display: 'flex',
                  justify: 'space-between',
                  alignItems: 'center',
                  padding: '1rem 1.5rem',
                  borderTop: '1px solid var(--border-color)',
                  background: 'var(--surface-hover, rgba(255,255,255,0.02))',
                  flexWrap: 'wrap',
                  gap: '1rem'
                }}>
                  <div style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>
                    Showing <span style={{ color: 'var(--text-main)', fontWeight: '600' }}>{indexOfFirstTicket + 1}</span> to{' '}
                    <span style={{ color: 'var(--text-main)', fontWeight: '600' }}>{Math.min(indexOfLastTicket, tickets.length)}</span> of{' '}
                    <span style={{ color: 'var(--text-main)', fontWeight: '600' }}>{tickets.length}</span> tickets
                  </div>

                  {totalPages > 1 && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <button
                        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                        disabled={currentPage === 1}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.25rem',
                          padding: '0.4rem 0.75rem',
                          borderRadius: '6px',
                          border: '1px solid var(--border-color)',
                          background: 'var(--bg-input)',
                          color: currentPage === 1 ? 'var(--text-muted)' : 'var(--text-main)',
                          cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                          opacity: currentPage === 1 ? 0.5 : 1,
                          fontSize: '0.85rem',
                          fontWeight: '500'
                        }}
                      >
                        <ChevronLeft size={16} /> Previous
                      </button>

                      {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                        <button
                          key={page}
                          onClick={() => setCurrentPage(page)}
                          style={{
                            padding: '0.4rem 0.75rem',
                            borderRadius: '6px',
                            border: '1px solid',
                            borderColor: currentPage === page ? 'var(--primary)' : 'var(--border-color)',
                            background: currentPage === page ? 'var(--primary)' : 'transparent',
                            color: currentPage === page ? '#ffffff' : 'var(--text-main)',
                            cursor: 'pointer',
                            fontSize: '0.85rem',
                            fontWeight: '600'
                          }}
                        >
                          {page}
                        </button>
                      ))}

                      <button
                        onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                        disabled={currentPage === totalPages}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.25rem',
                          padding: '0.4rem 0.75rem',
                          borderRadius: '6px',
                          border: '1px solid var(--border-color)',
                          background: 'var(--bg-input)',
                          color: currentPage === totalPages ? 'var(--text-muted)' : 'var(--text-main)',
                          cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                          opacity: currentPage === totalPages ? 0.5 : 1,
                          fontSize: '0.85rem',
                          fontWeight: '500'
                        }}
                      >
                        Next <ChevronRight size={16} />
                      </button>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* View Ticket Details Modal (Rendered via React Portal at Document Body to prevent sidebar clipping) */}
      {selectedTicket && createPortal(
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: '100vw',
            height: '100vh',
            background: 'rgba(0, 0, 0, 0.75)',
            display: 'flex',
            alignItems: 'center',
            justify: 'center',
            zIndex: 99999,
            padding: '1rem',
            boxSizing: 'border-box',
            backdropFilter: 'blur(6px)'
          }}
          onClick={() => setSelectedTicket(null)}
        >
          <div 
            style={{
              position: 'relative',
              background: 'var(--bg, #111318)',
              border: '1px solid var(--border, rgba(255, 255, 255, 0.12))',
              borderRadius: '16px',
              maxWidth: '650px',
              width: '100%',
              maxHeight: '90vh',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.7)',
              color: 'var(--text-main, #f8fafc)',
              margin: 'auto'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Top Right Close (Cross) Button */}
            <button 
              onClick={() => setSelectedTicket(null)} 
              title="Close Modal"
              style={{ 
                position: 'absolute',
                top: '1rem',
                right: '1.25rem',
                background: 'rgba(255, 255, 255, 0.08)', 
                border: '1px solid rgba(255, 255, 255, 0.15)', 
                color: 'var(--text-muted, #94a3b8)', 
                cursor: 'pointer', 
                padding: '0.4rem', 
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justify: 'center',
                zIndex: 10,
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(239, 68, 68, 0.2)';
                e.currentTarget.style.color = '#ef4444';
                e.currentTarget.style.borderColor = 'rgba(239, 68, 68, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.08)';
                e.currentTarget.style.color = 'var(--text-muted, #94a3b8)';
                e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.15)';
              }}
            >
              <X size={18} />
            </button>

            {/* Modal Header */}
            <div style={{
              padding: '1.25rem 3.5rem 1.25rem 1.5rem',
              borderBottom: '1px solid var(--border, rgba(255, 255, 255, 0.12))',
              display: 'flex',
              justify: 'space-between',
              alignItems: 'center',
              background: 'var(--surface-hover, rgba(255, 255, 255, 0.05))'
            }}>
              <div>
                <div style={{ fontSize: '0.8rem', color: 'var(--text-muted, #94a3b8)', fontFamily: 'monospace', marginBottom: '0.2rem' }}>
                  Ticket #{selectedTicket.publicId || selectedTicket.id}
                </div>
                <h3 style={{ margin: 0, fontSize: '1.2rem', color: 'var(--text-main, #f8fafc)', fontWeight: '600' }}>
                  {selectedTicket.subject}
                </h3>
              </div>
            </div>

            {/* Modal Body */}
            <div style={{ padding: '1.5rem', overflowY: 'auto', flex: 1 }}>
              <div style={{ display: 'flex', gap: '1.5rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
                <div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-muted, #94a3b8)', textTransform: 'uppercase', fontWeight: '600', marginBottom: '0.3rem' }}>Priority</div>
                  <span style={{ 
                    padding: '0.2rem 0.6rem', 
                    borderRadius: '4px', 
                    fontSize: '0.75rem',
                    fontWeight: '700',
                    background: selectedTicket.priority === 'HIGH' || selectedTicket.priority === 'URGENT' ? 'rgba(239, 68, 68, 0.15)' : 'rgba(34, 197, 94, 0.15)',
                    color: selectedTicket.priority === 'HIGH' || selectedTicket.priority === 'URGENT' ? '#ef4444' : '#4ade80'
                  }}>
                    {selectedTicket.priority}
                  </span>
                </div>

                <div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-muted, #94a3b8)', textTransform: 'uppercase', fontWeight: '600', marginBottom: '0.3rem' }}>Status</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', color: selectedTicket.status === 'OPEN' ? '#ef4444' : selectedTicket.status === 'IN_PROGRESS' ? '#f59e0b' : '#10b981', fontWeight: '600', fontSize: '0.85rem' }}>
                    {selectedTicket.status === 'OPEN' ? <AlertCircle size={14} /> : selectedTicket.status === 'IN_PROGRESS' ? <Clock size={14} /> : <CheckCircle2 size={14} />}
                    {selectedTicket.status === 'OPEN' ? 'Open' : selectedTicket.status === 'IN_PROGRESS' ? 'In Progress' : 'Resolved'}
                  </div>
                </div>

                <div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-muted, #94a3b8)', textTransform: 'uppercase', fontWeight: '600', marginBottom: '0.3rem' }}>Created Date</div>
                  <div style={{ color: 'var(--text-main, #f8fafc)', fontSize: '0.85rem', fontWeight: '500' }}>
                    {new Date(selectedTicket.createdAt).toLocaleString()}
                  </div>
                </div>
              </div>

              <div>
                <div style={{ fontSize: '0.85rem', fontWeight: '600', color: 'var(--text-muted, #94a3b8)', marginBottom: '0.5rem' }}>
                  Description / Message
                </div>
                <div style={{ 
                  background: 'var(--surface-3, rgba(255, 255, 255, 0.04))', 
                  padding: '1rem', 
                  borderRadius: '8px', 
                  border: '1px solid var(--border, rgba(255, 255, 255, 0.1))', 
                  color: 'var(--text-main, #f8fafc)', 
                  fontSize: '0.95rem',
                  lineHeight: '1.6',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  minHeight: '100px'
                }}>
                  {selectedTicket.description || <span style={{ color: 'var(--text-muted, #94a3b8)', fontStyle: 'italic' }}>No description provided.</span>}
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div style={{ padding: '1rem 1.5rem', borderTop: '1px solid var(--border, rgba(255, 255, 255, 0.12))', display: 'flex', justifyContent: 'flex-end', background: 'var(--surface-hover, rgba(255, 255, 255, 0.05))' }}>
              <button 
                onClick={() => setSelectedTicket(null)} 
                className="btn btn-secondary" 
                style={{ padding: '0.5rem 1.5rem' }}
              >
                Close
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

export default Support;


